import React, { Component } from 'react'
import isIn from 'validator/lib/isIn'
import { connect } from 'react-redux'
import { withRouter as withRouterBase } from 'react-router-dom'
import { isWebView } from './admin/actions/index'
import {
    I18nextProvider,
    Translation,
} from 'react-i18next'
import translates from './locales/i18n'
import Configurator from './configurator'
import { RESPONSIVE_WIDTH } from './consts'

/**
 * Check if component is for system
 * If component is not "forSystem", returns null
 * USAGE - @forSystem(S_SPORTNECT)  OR  @forSystem([ S_SPORTNECT, S_SPORTUJ_V_OSTRAVE ])
 *
 * @param {string|Array} system
 * @return {Function}
 */
export function forSystem(system) {
    return function(Child) {
        class Validator extends Component {
            constructor(props) {
                super(props)
            }

            render() {
                if (Array.isArray(system)) {
                    if (!isIn(window.APP_CONFIG.REACT_APP_SYSTEM, system)) {
                        return null
                    }
                } else {
                    if (window.APP_CONFIG.REACT_APP_SYSTEM !== system) {
                        return null
                    }
                }

                return <Child {...this.props} {...this.context} />
            }
        }

        return Validator
    }
}

/**
 * Check if component is for feature defined in config
 * USAGE - @forFeature('FOO_BAR')
 *
 * @param {string|Array} featureName - name of config key
 * @param {string|int|boolean|null} expectedValue - what config key may be
 * @return {Function}
 */
export function forFeature(featureName, expectedValue = true) {
    return function(Child) {
        class Validator extends Component {
            constructor(props) {
                super(props)
            }

            render() {
                const appConfig = window.APP_CONFIG
                expectedValue = expectedValue + ''

                if (Array.isArray(featureName)) {
                    const featureResults = featureName.map(feature => (appConfig['REACT_APP_' + feature] || '') + '')
                    if (!isIn(expectedValue, featureResults)) {
                        return null
                    }
                } else {
                    if (expectedValue !== (appConfig['REACT_APP_' + featureName] || '') + '') {
                        return null
                    }
                }

                return <Child {...this.props} {...this.context} />
            }
        }

        return Validator
    }
}

/**
 * Check if component is for feature defined in config
 * USAGE - @forFeature('FOO_BAR')
 *
 * @param {string|Array} featureName - name of config key
 * @param {string|int|boolean|null} expectedValue - what config key may be
 * @return {Function}
 */
export function forFeatureWrapper(Child, featureName, expectedValue = true) {
    return class extends Component {
        constructor(props) {
            super(props)
        }

        render() {
            const appConfig = window.APP_CONFIG
            expectedValue = expectedValue + ''

            if (Array.isArray(featureName)) {
                const featureResults = featureName.map(feature => (appConfig['REACT_APP_' + feature] || '') + '')
                if (!isIn(expectedValue, featureResults)) {
                    return null
                }
            } else {
                if (expectedValue !== (appConfig['REACT_APP_' + featureName] || '') + '') {
                    return null
                }
            }

            return <Child {...this.props} {...this.context} />
        }
    }
}

/**
 * Check if component is for web view
 * USAGE - @forWebView(false)
 *
 * @param {boolean|null} expectedValue - render child if is web view expected value
 * @return {Function}
 */
export function forWebView(expectedValue = true) {
    return function(Child) {
        class Validator extends Component {
            render() {
                if (isWebView() === expectedValue) {
                    return <Child {...this.props} {...this.context} />
                }

                return null
            }
        }

        return Validator
    }
}

/**
 * Apply global config to class
 * USAGE - @config()    - now will be config applied to class and will be available on props - this.props.config
 *
 * @return {Function}
 */
export function config() {
    return function(Child) {
        class Config extends Component {
            render() {
                return <Child {...this.props} {...this.context} config={window.APP_CONFIG} />
            }
        }

        return Config
    }
}

/**
 * Apply logged user from store
 * USAGE - @authedUser()    - now will be authed user applied to props
 *
 * @return {Function}
 */
export function authedUserConnect() {
    return function(Child) {
        class AuthedUser extends Component {
            render() {
                return <Child {...this.props} {...this.context} />
            }
        }

        return connect(({ auth }) => ({
            authedUser: auth.user,
        }), {})(AuthedUser)
    }
}

/**
 * Apply i18n to child
 * USAGE - @i18n()    - now will be translations available in class
 *
 * @return {Function}
 */
export function i18n() {
    return function(Child) {
        class I18N extends Component {
            render() {
                return (
                    <I18nextProvider i18n={translates}>
                        <Translation>
                            {t => <Child t={t} {...this.props} {...this.context} />}
                        </Translation>
                    </I18nextProvider>
                )
            }
        }

        return I18N
    }
}

/**
 * Make i18n wrapper for usage as export
 * USAGE - export default i18nWrapper(Class)
 *
 * @param {*} Child
 * @return {*}
 */
export function i18nWrapper(Child) {
    return class extends Component {
        render() {
            return (
                <I18nextProvider i18n={translates}>
                    <Translation>
                        {t => <Child t={t} {...this.props} {...this.context} />}
                    </Translation>
                </I18nextProvider>
            )
        }
    }
}

/**
 * Replace base redux connect by connect with router applied
 * USAGE - export default withRouterConnectWrapper(state => { foo: state.bar })(Class)
 *
 * @param {Function} mapStateToProps
 * @param {Function|null} mapDispatchToProps
 * @return {Function}
 */
export function withRouterConnectWrapper(mapStateToProps, mapDispatchToProps = null) {
    return function(WrappedComponent) {
        return withRouterBase(connect(mapStateToProps, mapDispatchToProps)(WrappedComponent))
    }
}

/**
 * Replace base redux connect by simple connect
 * USAGE - export default withConnectWrapper(state => { foo: state.bar })(Class)
 *
 * @param {Function} mapStateToProps
 * @param {Function|null} mapDispatchToProps
 * @return {Function}
 */
export function withConnectWrapper(mapStateToProps, mapDispatchToProps = null) {
    return function(WrappedComponent) {
        return connect(mapStateToProps, mapDispatchToProps)(WrappedComponent)
    }
}

/**
 * Apply with router to class
 * USAGE - @withRouter()    - now will be withRouter inside function
 *
 * @return {Function}
 */
export function withRouter() {
    return function(Child) {
        class Class extends Component {
            render = () => <Child {...this.props} {...this.context} />
        }

        return withRouterBase(Class)
    }
}

/**
 * Render child for desktop or responsive variant, depend on param renderInReponsive
 * @param {boolean} renderInResponsive
 * @returns {function(*): RenderInResponsive}
 */
export function shouldRenderInResponsive(renderInResponsive) {
    return function(Child) {
        class RenderInResponsive extends Configurator {
            render = () => {
                const isResponsive = this.config.windowWidth <= RESPONSIVE_WIDTH

                if (renderInResponsive && isResponsive) {
                    return <Child {...this.props} />
                }

                if (!renderInResponsive && !isResponsive) {
                    return <Child {...this.props} />
                }

                return null
            }
        }

        return RenderInResponsive
    }
}

/**
 * Connect store errors object into child
 * @returns {function(*)}
 */
export function withErrorsConnect() {
    return function(Child) {
        class WithErrors extends Component {
            render() {
                return <Child {...this.props} />
            }
        }

        return connect(state => ({
            errors: state.errors,
        }))(WithErrors)
    }
}

/**
 * Connect store errors object into child
 * @returns {function(*)}
 */
export function withLocaleConnect() {
    return function(Child) {
        class WithLocale extends Component {
            render() {
                return <Child {...this.props} />
            }
        }

        return connect(state => ({
            locale: state.locale,
        }))(WithLocale)
    }
}
